/**
 * ReportSettingsMenu — a gear popover that holds the report editor's rare /
 * advanced controls so the primary control bar stays calm:
 *   - AI suggestions mode (Inline / Strip / No AI)
 *   - Show / hide normal fields
 *   - Show / hide review-tier ("maybe") findings (default hidden)
 *   - Mic settings (opens the STT pipeline test dialog)
 *   - Reset draft (destructive)
 *
 * These used to crowd the top row (the A/B toggle) and the bottom toolbar
 * (the eye + trash icons). Folding them behind a gear keeps the common path
 * — dictate, regenerate impression, sign — uncluttered.
 */

import { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { Link } from "react-router-dom";
import { ReporterModeToggle } from "../reporter/ReporterModeToggle";
import { showLowConfidenceFindingsAtom, type ReporterAiMode } from "../../atoms/reporterPrefsAtoms";
import { FaCog } from "../../utils/icons";

interface ReportSettingsMenuProps {
  reporterAiMode: ReporterAiMode;
  onChangeMode: (mode: ReporterAiMode) => void;
  showNormalFields: boolean;
  onToggleNormalFields: () => void;
  onClearDraft: () => void;
  onOpenMicSettings: () => void;
  /** AI-template controls (mode, normals, reset) only render with report data. */
  hasReportData: boolean;
  /** Mic settings only render once auth is ready (dictation available). */
  dictationActive: boolean;
  /** Templates link only renders for roles the BE /api/my-templates allows. */
  canEditTemplates: boolean;
}

export function ReportSettingsMenu({
  reporterAiMode,
  onChangeMode,
  showNormalFields,
  onToggleNormalFields,
  onClearDraft,
  onOpenMicSettings,
  hasReportData,
  dictationActive,
  canEditTemplates,
}: ReportSettingsMenuProps) {
  const [open, setOpen] = useState(false);
  const [showLowConfidence, setShowLowConfidence] = useAtom(showLowConfidenceFindingsAtom);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocPointer(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!hasReportData && !dictationActive && !canEditTemplates) return null;

  return (
    <div className="report-settings" ref={ref}>
      <button
        type="button"
        className={`report-btn report-btn--icon ${open ? "report-btn--toggle-active" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Report settings"
        aria-label="Report settings"
        onClick={() => setOpen((v) => !v)}
      >
        <FaCog className="w-4 h-4" />
      </button>

      {open && (
        <div className="report-settings__panel" role="menu">
          {hasReportData && (
            <>
              <span className="report-settings__section-label">AI suggestions</span>
              <ReporterModeToggle mode={reporterAiMode} onChange={onChangeMode} />

              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={showNormalFields}
                className="report-settings__item"
                onClick={onToggleNormalFields}
              >
                <span>Show normal fields</span>
                <span aria-hidden="true">{showNormalFields ? "On" : "Off"}</span>
              </button>

              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={showLowConfidence}
                className="report-settings__item"
                onClick={() => setShowLowConfidence((v) => !v)}
              >
                <span>Show review-tier findings</span>
                <span aria-hidden="true">{showLowConfidence ? "On" : "Off"}</span>
              </button>
            </>
          )}

          {canEditTemplates && (
            <Link
              to="/my-templates"
              role="menuitem"
              className="report-settings__item"
              onClick={() => setOpen(false)}
            >
              <span>Templates</span>
            </Link>
          )}

          {dictationActive && (
            <button
              type="button"
              role="menuitem"
              className="report-settings__item"
              onClick={() => {
                onOpenMicSettings();
                setOpen(false);
              }}
            >
              <span>Mic settings</span>
            </button>
          )}

          {hasReportData && (
            <>
              <div className="report-settings__divider" />
              <button
                type="button"
                role="menuitem"
                className="report-settings__item report-settings__item--destructive"
                onClick={() => {
                  onClearDraft();
                  setOpen(false);
                }}
              >
                <span>Reset draft</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
